// AttendeeList.tsx
/* eslint-disable */
import React, { useCallback, useRef, useState } from 'react';
import { Registration, UpdateRegistrationInput } from './API';
import { updateRegistrationDetails } from './mutations/processUpdateRegistration';
import DownloadModal from './DownloadModal';
import Switch from "react-switch";
import { Select, Input, Checkbox, SearchIcon } from '@uclaea/react-component-library';
import './css/AttendeeList.css';
import AttendeeRow from './components/AttendeeRow';
import BulkActions from './components/BulkActions';
import PaginationControls from './components/PaginationControls';
import { formatDate } from './utils/formatDate';
import { parseAdditionalData, serializeAdditionalData } from './utils/parseAdditionalData';
import { calculateCheckInStatus } from './utils/registrationStatus';
import { useAttendeeFiltering } from './hooks/useAttendeeFiltering';
import { useAttendeeSelection } from './hooks/useAttendeeSelection';
import { PartyAttendanceRows } from './components/PartyAttendanceRows';
import { isIndividual, mergeParty } from './utils/partyAttendance';

interface AttendeeListProps {
  attendees: Registration[];
  setAttendees: React.Dispatch<React.SetStateAction<Registration[]>>;
}

function AttendeeList(props: AttendeeListProps) {
  const partyCheckIns = useRef(new Map<string, () => Promise<boolean>>());
  const registerPartyCheckIn = useCallback((id: string, checkIn: (() => Promise<boolean>) | null) => {
    if (checkIn) partyCheckIns.current.set(id, checkIn);
    else partyCheckIns.current.delete(id);
  }, []);
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null);
  const [rowActions, setRowActions] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});

  const {
    setSearchQuery,
    testToggled, handleTestToggleChange,
    statusFilter, setStatusFilter,
    handleSort,
    currentPage, handleNextPage, handlePreviousPage,
    filteredAttendees, paginatedAttendees, totalPages,
  } = useAttendeeFiltering(props.attendees);

  const handleSubmit = async (newCheckedInCount: number, attendee: Registration) => {
    if (isIndividual(attendee)) {
      if (newCheckedInCount === 0) throw new Error('Undo individual attendance one person at a time.');
      const checkIn = partyCheckIns.current.get(attendee.registrationId);
      if (!checkIn || !await checkIn()) throw new Error('Review this party’s row for details or retry the pending action.');
      return;
    }
    const registrationData: UpdateRegistrationInput = {
      eventId: attendee.eventId,
      registrationId: attendee.registrationId,
      checkedInCount: newCheckedInCount,
      registrationStatus: calculateCheckInStatus(newCheckedInCount, attendee.guests ?? 0),
    };
    try {
      const response = await updateRegistrationDetails(registrationData);
      if (response && response.checkedInCount === newCheckedInCount) {
        props.setAttendees(prevAttendees =>
          prevAttendees.map(a =>
            a.registrationId === attendee.registrationId
              ? { ...a, checkedInCount: newCheckedInCount, registrationStatus: registrationData.registrationStatus }
              : a
          )
        );
        setRowActions(prevRowActions => ({ ...prevRowActions, [attendee.registrationId]: 0 }));
      } else {
        throw new Error('The registration could not be confirmed. Refresh before trying again.');
      }
      setActiveDropdownId(null);
    } catch (err) {
      console.error('Error updating registration:', err);
      setActiveDropdownId(null);
      throw err;
    }
  };

  const {
    selectedAttendees,
    bulkAction, setBulkAction,
    showDownloadModal, setShowDownloadModal,
    selectedDownloadFormat, setSelectedDownloadFormat,
    toggleSelectAll, toggleSelectOne,
    handleBulkSubmit, handleDownloadConfirm, bulkBusy, bulkError, allowUndo,
  } = useAttendeeSelection(props.attendees, props.setAttendees, filteredAttendees, handleSubmit);

  const visibleIds = new Set(paginatedAttendees.map(p => p.registrationId));
  const tableRegistrations = [...paginatedAttendees, ...props.attendees
    .filter(p => isIndividual(p) && !visibleIds.has(p.registrationId))
    .map(p => ({ ...p, guests: p.guests ?? 0, checkedInCount: p.checkedInCount ?? 0, notes: '', membership: '' }))];

  const handleSaveRow = async (attendee: Registration & { notes?: string; membership?: string }) => {
    if (isIndividual(attendee)) return;
    const additionalDataObj = parseAdditionalData(attendee.additionalData);
    const { membership, notes: attendeeNotes } = attendee;
    additionalDataObj.notes = notes[attendee.registrationId] !== undefined ? notes[attendee.registrationId] : attendeeNotes || '';
    if (membership) {
      additionalDataObj.membership = membership;
    }
    const newAdditionalData = serializeAdditionalData(additionalDataObj);
    const newCheckedInCount = rowActions[attendee.registrationId] ?? attendee.checkedInCount;
    const registrationData: UpdateRegistrationInput = {
      eventId: attendee.eventId,
      registrationId: attendee.registrationId,
      checkedInCount: newCheckedInCount,
      registrationStatus: calculateCheckInStatus(newCheckedInCount, attendee.guests ?? 0),
      additionalData: newAdditionalData,
    };
    try {
      await updateRegistrationDetails(registrationData);
      props.setAttendees(prevAttendees =>
        prevAttendees.map(a =>
          a.registrationId === attendee.registrationId
            ? { ...a, checkedInCount: newCheckedInCount, registrationStatus: registrationData.registrationStatus, additionalData: newAdditionalData }
            : a
        )
      );
      setRowActions(prevRowActions => ({ ...prevRowActions, [attendee.registrationId]: 0 }));
    } catch (err) {
      console.error('Error updating registration:', err);
    }
  };

  return (
    <>
      <div className='registrations-options-container'>
        <h2 className="registrations-options-header">Registrants</h2>
        <div className='registrations-status-container'>
          <p className='registrations-status-header-text'>Status:</p>
          <p className='registrations-status-value-text'>
            {props.attendees.filter((reg) => reg.registrationStatus === "CHECKED_IN" || reg.registrationStatus === "PARTIAL_CHECK_IN").length}/{props.attendees.length}
          </p>
        </div>
      </div>
      <div className='attendee-list-container'>
        <div className="toggle-row">
          <h4 className="attendee-header">Filter by Status</h4>
          <div className="filter-status-select">
            <Select
              label=""
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { label: 'All', id: 'all', value: 'ALL' },
                { label: 'Registered', id: 'registered', value: 'REGISTERED' },
                { label: 'Checked In', id: 'checked-in', value: 'CHECKED_IN' },
                { label: 'Partial Check-In', id: 'partial', value: 'PARTIAL_CHECK_IN' },
                { label: 'Cancelled', id: 'cancelled', value: 'CANCELLED' },
                { label: 'Invited', id: 'invited', value: 'INVITED' },
              ]}
            />
          </div>
        </div>
        <div className="toggle-row">
          <h4 className="attendee-header">Show Test Registrations</h4>
          <Switch onChange={handleTestToggleChange} checked={testToggled} />
        </div>
        <Input
          label="Search"
          type="text"
          placeholder="Search registrants and guests by name or email..."
          onChange={(e) => setSearchQuery(e.target.value)}
          icon={{ left: SearchIcon }}
        />
        <BulkActions bulkAction={bulkAction} setBulkAction={setBulkAction} onSubmit={handleBulkSubmit}
          busy={bulkBusy} allowUndo={allowUndo} />
        {bulkBusy && <p role="status">Saving attendance for selected registrations…</p>}
        {bulkError && <p role="alert">{bulkError}</p>}
        {!allowUndo && <p>Check In includes the entire selected party. Undo individual attendance from each person’s row.</p>}
        <table className="attendee-table">
          <thead>
            <tr>
              <th>
                <Checkbox
                  label=""
                  onChange={e => toggleSelectAll((e.target as HTMLInputElement).checked)}
                  checked={filteredAttendees.length > 0 && filteredAttendees.every(p => selectedAttendees.has(p.registrationId))}
                  disabled={bulkBusy}
                  aria-label="Select all registrations with bulk actions"
                />
              </th>
              <th onClick={() => handleSort('lastName')}>Name</th>
              <th onClick={() => handleSort('email')}>Email</th>
              <th onClick={() => handleSort('dateCreated')} className='hide-for-mobile'>Transaction date</th>
              <th>Status</th>
              <th onClick={() => handleSort('guests')} className='hide-for-mobile'>Guests</th>
              <th onClick={() => handleSort('notes')} className='hide-for-mobile'>Notes</th>
              <th>Check In</th>
            </tr>
          </thead>
          <tbody>
            {!paginatedAttendees.length && <tr><td colSpan={8}>No matching registrants.</td></tr>}
            {tableRegistrations.map(attendee => isIndividual(attendee) ? (
              <PartyAttendanceRows key={attendee.registrationId} party={attendee} notes={attendee.notes}
                visible={visibleIds.has(attendee.registrationId)} onRegisterCheckIn={registerPartyCheckIn}
                selected={selectedAttendees.has(attendee.registrationId)} onSelect={() => toggleSelectOne(attendee.registrationId)} bulkBusy={bulkBusy}
                onChange={party => props.setAttendees(previous => mergeParty(previous, party))} />
            ) : (
              <AttendeeRow
                key={attendee.registrationId}
                attendee={attendee}
                selected={selectedAttendees.has(attendee.registrationId)}
                onSelect={() => toggleSelectOne(attendee.registrationId)}
                notesValue={notes[attendee.registrationId] !== undefined ? notes[attendee.registrationId] : attendee.notes || ''}
                onNotesChange={val => setNotes(prev => ({ ...prev, [attendee.registrationId]: val }))}
                onSave={() => handleSaveRow(attendee)}
                rowAction={rowActions[attendee.registrationId] ?? attendee.checkedInCount}
                onRowActionChange={val => setRowActions({ ...rowActions, [attendee.registrationId]: val })}
                disabled={bulkBusy || attendee.registrationStatus === 'CANCELLED'}
                formatDate={formatDate}
                activeDropdownId={activeDropdownId}
              />
            ))}
          </tbody>
        </table>
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevious={handlePreviousPage}
          onNext={handleNextPage}
        />
      </div>
      {showDownloadModal && (
        <DownloadModal
          selectedDownloadFormat={selectedDownloadFormat}
          setSelectedDownloadFormat={setSelectedDownloadFormat}
          onConfirm={handleDownloadConfirm}
          onCancel={() => setShowDownloadModal(false)}
        />
      )}
    </>
  );
}

export default AttendeeList;
